import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { MarkdownContent } from '../components/MarkdownContent';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Layers, GraduationCap, LifeBuoy, ArrowRight } from 'lucide-react';

export const DocsHomePage: React.FC = () => {
  const sections = [
    {
      icon: BookOpen,
      title: 'Getting Started',
      description: 'Learn how to set up your account and start using Vehix',
      link: '/docs/getting-started',
      topics: ['Account Creation', 'First Login', 'Understanding Roles', 'Quick Start Guides']
    },
    {
      icon: Layers,
      title: 'Features',
      description: 'Explore all the powerful features of the Vehix platform',
      link: '/docs/features',
      topics: ['Recall Map', 'Manual Routing', 'Route Tracker', 'Campaign Management']
    },
    {
      icon: GraduationCap,
      title: 'Guides & Tutorials',
      description: 'Step-by-step guides for common workflows and tasks',
      link: '/docs/guides',
      topics: ['How-To Guides', 'Daily Checklists', 'Tips & Tricks', 'FAQ']
    },
    {
      icon: LifeBuoy,
      title: 'Support',
      description: 'Get help and learn about our support policies',
      link: '/docs/support',
      topics: ['Support Policy', 'Contact Information', 'Report Issues', 'System Status']
    }
  ];

  return (
    <DocsLayout
      title='Vehix Documentation'
      description='Everything you need to know about using the Vehix recall intelligence platform'
      breadcrumbs={[{ label: 'Documentation' }]}
    >
      <MarkdownContent>
        <div className='not-prose max-w-[1000px]'>
          <section className='mb-20'>
            <div className='grid gap-6 md:grid-cols-2'>
              {sections.map(section => {
                const Icon = section.icon;
                return (
                  <Link key={section.title} to={section.link}>
                    <Card className='h-full hover:border-primary transition-colors cursor-pointer'>
                      <CardHeader>
                        <div className='flex items-center gap-3 mb-2'>
                          <div className='p-2 bg-primary/10 rounded-lg'>
                            <Icon className='h-8 w-8 text-primary' />
                          </div>
                          <CardTitle className='text-lg font-bold'>{section.title}</CardTitle>
                        </div>
                        <CardDescription className='text-sm leading-6'>
                          {section.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className='space-y-2 text-sm text-muted-foreground'>
                          {section.topics.map(topic => (
                            <li key={topic} className='flex items-center gap-2'>
                              <ArrowRight className='h-3 w-3 text-primary' />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>

          <section className='mb-20'>
            <div className='bg-muted/50 border border-border rounded-lg p-6'>
              <h2 className='text-3xl font-bold mb-6'>Quick Links</h2>
              <div className='grid gap-4 md:grid-cols-3'>
                <Link
                  to='/docs/getting-started'
                  className='text-primary hover:underline flex items-center gap-2 text-sm'
                >
                  <ArrowRight className='h-4 w-4' />
                  New User? Start Here
                </Link>
                <Link
                  to='/docs/features/filters'
                  className='text-primary hover:underline flex items-center gap-2 text-sm'
                >
                  <ArrowRight className='h-4 w-4' />
                  Using Filters
                </Link>
                <Link
                  to='/docs/features/manual-routing'
                  className='text-primary hover:underline flex items-center gap-2 text-sm'
                >
                  <ArrowRight className='h-4 w-4' />
                  Route Assignment
                </Link>
              </div>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>About Vehix</h2>
            <p className='text-sm leading-6 mb-4'>
              Vehix is a comprehensive platform that helps automotive dealerships manage vehicle
              safety recalls efficiently. The system provides:
            </p>
            <ul className='space-y-2 text-sm leading-6 mb-8'>
              <li className='flex items-start gap-2'>
                <span className='text-primary mt-1'>•</span>
                <span>
                  <strong>Geographic visualization</strong> of recalls across dealer networks
                </span>
              </li>
              <li className='flex items-start gap-2'>
                <span className='text-primary mt-1'>•</span>
                <span>
                  <strong>Smart routing</strong> and technician assignment for mobile recall
                  services
                </span>
              </li>
              <li className='flex items-start gap-2'>
                <span className='text-primary mt-1'>•</span>
                <span>
                  <strong>Real-time tracking</strong> of recall completion and performance metrics
                </span>
              </li>
              <li className='flex items-start gap-2'>
                <span className='text-primary mt-1'>•</span>
                <span>
                  <strong>Dealership management</strong> with role-based access control
                </span>
              </li>
              <li className='flex items-start gap-2'>
                <span className='text-primary mt-1'>•</span>
                <span>
                  <strong>Campaign configuration</strong> and prioritization
                </span>
              </li>
            </ul>

            <h2 className='text-3xl font-bold mb-6'>Need Help?</h2>
            <p className='text-sm leading-6'>
              If you can't find what you're looking for in the documentation, please visit our{' '}
              <Link to='/docs/support' className='text-primary hover:underline'>
                Support page
              </Link>{' '}
              to learn how to get in touch with our team.
            </p>
          </section>
        </div>
      </MarkdownContent>
    </DocsLayout>
  );
};
